import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  ArrowLeft, Loader2, Sparkles, ShieldAlert, MessageSquare, CheckCircle2, XCircle, Bot,
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORY_COLOR = {
  payment_dispute: 'bg-rose-100 text-rose-700',
  missing_payment: 'bg-rose-100 text-rose-700',
  hours: 'bg-amber-100 text-amber-700',
  safety: 'bg-red-200 text-red-800',
  harassment: 'bg-red-200 text-red-800',
  injury: 'bg-red-200 text-red-800',
  client_complaint: 'bg-orange-100 text-orange-700',
  account_restriction: 'bg-purple-100 text-purple-700',
  contract_dispute: 'bg-indigo-100 text-indigo-700',
  legal: 'bg-indigo-100 text-indigo-700',
  idv: 'bg-blue-100 text-blue-700',
  fraud: 'bg-red-200 text-red-800',
  other: 'bg-slate-100 text-slate-600',
};

const CATEGORIES = [
  'payment_dispute', 'missing_payment', 'hours', 'safety', 'harassment', 'injury',
  'client_complaint', 'account_restriction', 'contract_dispute', 'legal', 'idv', 'fraud', 'other',
];

const AiEscalations = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('open');
  const [category, setCategory] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, s] = await Promise.all([
        api.get('/admin/ai-support/escalations', { params: { status, category } }),
        api.get('/admin/ai-support/summary'),
      ]);
      setItems(e.data?.escalations || []);
      setSummary(s.data || {});
    } catch (err) {
      toast.error('Failed to load AI escalations');
    } finally {
      setLoading(false);
    }
  }, [status, category]);

  useEffect(() => { load(); }, [status, category]); // eslint-disable-line

  const review = async (id, review_status) => {
    try {
      await api.post(`/admin/ai-support/escalations/${id}/resolve`, { review_status });
      toast.success(review_status === 'resolved' ? 'Marked resolved' : 'Dismissed');
      load();
    } catch (err) { toast.error('Failed to update'); }
  };

  const stat = (label, value, Icon, tint) => (
    <div className="bg-white rounded-xl border border-slate-200 p-4 flex items-center gap-3" data-testid={`ai-esc-stat-${label}`}>
      <div className={`p-2 rounded-lg ${tint}`}><Icon className="w-5 h-5" /></div>
      <div>
        <div className="text-2xl font-bold text-slate-900">{value ?? 0}</div>
        <div className="text-xs text-slate-500">{label}</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 pb-16" data-testid="admin-ai-escalations-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/messages')} data-testid="ai-esc-back-btn">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold text-slate-900 flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-indigo-600" /> Evie Support Escalations
            </h1>
            <p className="text-xs text-slate-500">Questions Evie (Virtual Assistant) sent to a human. Review, reply in the DM, and resolve.</p>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-4 space-y-4">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {stat('Open escalations', summary.open_escalations, ShieldAlert, 'bg-rose-100 text-rose-700')}
          {stat('Answered by Evie', summary.answered_by_ai, Bot, 'bg-indigo-100 text-indigo-700')}
          {stat('Total escalations', summary.total_escalations, MessageSquare, 'bg-amber-100 text-amber-700')}
          {stat('Evie deflection %', `${summary.deflection_rate ?? 0}%`, CheckCircle2, 'bg-emerald-100 text-emerald-700')}
        </div>

        <div className="flex items-center gap-2 flex-wrap">
          <Select value={status} onValueChange={setStatus}>
            <SelectTrigger className="w-40 bg-white" data-testid="ai-esc-status-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="dismissed">Dismissed</SelectItem>
              <SelectItem value="all">All</SelectItem>
            </SelectContent>
          </Select>
          <Select value={category || 'all'} onValueChange={(v) => setCategory(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-48 bg-white" data-testid="ai-esc-category-filter"><SelectValue placeholder="All categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <div className="text-center py-20 text-slate-400"><Loader2 className="w-6 h-6 animate-spin inline mr-2" /> Loading…</div>
        ) : items.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 py-20 text-center text-slate-500" data-testid="ai-esc-empty">
            <Sparkles className="w-12 h-12 mx-auto mb-3 text-slate-300" />
            <div className="font-medium">No escalations here</div>
            <div className="text-xs mt-1">Evie (Virtual Assistant) is handling routine questions on its own.</div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="ai-esc-list">
            {items.map((e) => (
              <div key={e.id} className="bg-white rounded-xl border border-slate-200 p-4" data-testid={`ai-esc-item-${e.id}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-slate-900">{e.talent_name || e.talent_email}</span>
                    <Badge className={`${CATEGORY_COLOR[e.category] || CATEGORY_COLOR.other} border-0 text-[10px]`} data-testid={`ai-esc-cat-${e.id}`}>
                      {(e.category || 'other').replace(/_/g, ' ')}
                    </Badge>
                    {e.trigger === 'talent_request' && (
                      <Badge className="bg-slate-100 text-slate-600 border-0 text-[10px]">talent requested human</Badge>
                    )}
                    {e.trigger === 'safety_net' && (
                      <Badge className="bg-red-100 text-red-700 border-0 text-[10px]">safety-net</Badge>
                    )}
                    {typeof e.confidence === 'number' && (
                      <span className="text-[10px] text-slate-400">conf {(e.confidence * 100).toFixed(0)}%</span>
                    )}
                  </div>
                  <span className="text-[11px] text-slate-400">{e.created_at ? new Date(e.created_at).toLocaleString() : ''}</span>
                </div>

                <div className="mt-3 space-y-2">
                  <div className="text-sm bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                    <div className="text-[10px] font-semibold text-slate-500 mb-0.5">Talent asked</div>
                    <div className="text-slate-800 whitespace-pre-wrap break-words">{e.question}</div>
                  </div>
                  <div className="text-sm bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2">
                    <div className="text-[10px] font-semibold text-indigo-600 mb-0.5 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Evie replied</div>
                    <div className="text-indigo-950 whitespace-pre-wrap break-words">{e.ai_answer}</div>
                  </div>
                  {e.reason && (
                    <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
                      <span className="font-semibold">Why escalated:</span> {e.reason}
                    </div>
                  )}
                </div>

                <div className="mt-3 flex items-center gap-2 flex-wrap">
                  <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white"
                    onClick={() => navigate(`/admin/messages/dm/${e.talent_id}`)}
                    data-testid={`ai-esc-open-dm-${e.id}`}>
                    <MessageSquare className="w-3.5 h-3.5 mr-1" /> Open conversation
                  </Button>
                  {e.review_status === 'open' ? (
                    <>
                      <Button size="sm" variant="outline" className="border-emerald-300 text-emerald-700"
                        onClick={() => review(e.id, 'resolved')} data-testid={`ai-esc-resolve-${e.id}`}>
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Resolve
                      </Button>
                      <Button size="sm" variant="outline" className="border-slate-300 text-slate-600"
                        onClick={() => review(e.id, 'dismissed')} data-testid={`ai-esc-dismiss-${e.id}`}>
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Dismiss
                      </Button>
                    </>
                  ) : (
                    <Badge className="bg-slate-100 text-slate-600 border-0 text-[10px]">{e.review_status}</Badge>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AiEscalations;
